/**
 * Blog posts — SEO-optimized long-form content targeting high-intent
 * queries for Dubai off-plan real estate across Arabic, English, Russian.
 *
 * Each post has:
 *   - slug: URL slug (kebab-case, no language prefix — lang variants live under same slug)
 *   - lang: 'ar' | 'en' | 'ru'
 *   - title, description, keywords, image
 *   - publishedAt, updatedAt (ISO)
 *   - readingTime: minutes
 *   - content: HTML (structured with h2/h3, p, ul, table, faq)
 *   - faq: array of {q,a} for FAQ schema
 *   - translationKey: shared by the language versions of one article, so they
 *     can point at each other with hreflang (their slugs differ)
 *
 * A post lives under its own language's prefix: Arabic posts at /blog/<slug>,
 * English at /en/blog/<slug>, Russian at /ru/blog/<slug>.
 */

import { guidesAr } from './guides-ar.js';

export const posts = [
    // Newest Arabic guides first — they lead the Arabic blog list.
    ...guidesAr,

    // ============================================================
    // ARABIC
    // ============================================================
    {
        slug: 'guide-buying-off-plan-dubai-2026-ar',
        translationKey: 'off-plan-guide-2026',
        lang: 'ar',
        title: 'دليل شراء عقار على الخارطة في دبي 2026 — كل ما يجب معرفته',
        description: 'دليل شامل لشراء العقارات على الخارطة (Off-Plan) في دبي عام 2026: خطوات، خطط الدفع، حقوق المشتري، وأفضل المناطق للاستثمار.',
        keywords: 'شراء عقار دبي, عقارات على الخارطة, off plan dubai, خطة دفع 20/80, استثمار دبي, تملك حر, إقامة ذهبية',
        image: '/logo.png',
        publishedAt: '2026-07-15T10:00:00Z',
        updatedAt: '2026-07-15T10:00:00Z',
        readingTime: 9,
        excerpt: 'دبي في 2026 لا تزال الوجهة رقم واحد للاستثمار العقاري في الشرق الأوسط. هذا الدليل يشرح كيف تشتري عقارًا على الخارطة بأمان، ما هي خطط الدفع الشائعة، وكيف تحمي حقّك خطوة بخطوة.',
        content: `
<p class="lead">دبي في 2026 لا تزال الوجهة رقم واحد للاستثمار العقاري في الشرق الأوسط. سواء كنت تبحث عن سكن دائم، عائد إيجاري، أو نمو رأس المال — العقارات على الخارطة (Off-Plan) توفّر الأسعار الأدنى والفرص الأعلى. لكن الأمر يتطلّب فهمًا واضحًا لكيفيّة العمل.</p>

<h2>ما هو العقار على الخارطة (Off-Plan)؟</h2>
<p>العقار على الخارطة هو مشروع لم يكتمل بناؤه بعد. تشتريه من المطوّر مباشرةً بسعر أقلّ من السوق الجاهز، وتدفعه على أقساط طوال فترة الإنشاء، ثمّ تستلمه عند اكتمال المشروع (عادةً 2-4 سنوات).</p>

<h3>لماذا يفضّله المستثمرون؟</h3>
<ul>
  <li><strong>سعر منخفض</strong> — أقلّ بـ 20–35% من السوق الجاهز.</li>
  <li><strong>خطط دفع مرنة</strong> — أشهرها 20/80 و 60/40 و Post-Handover.</li>
  <li><strong>نموّ رأس المال</strong> — القيمة ترتفع خلال فترة الإنشاء.</li>
  <li><strong>خيارات أوسع</strong> — يمكن اختيار الوحدة والطابق والاتّجاه.</li>
</ul>

<h2>خطوات شراء عقار على الخارطة في دبي</h2>

<h3>1. اختر المطوّر</h3>
<p>أشهر المطوّرين في دبي: <strong>Emaar، DAMAC، Sobha، Nakheel، Meraas، Dubai Properties، Select Group، Azizi</strong>. كلّ مطوّر له سمعة ونمط عمل — Emaar يبني مشاريع أيقونيّة (Downtown، Marina)، DAMAC يميل للمشاريع الفاخرة، Sobha شهير بالجودة الإنشائيّة.</p>

<h3>2. اختر المنطقة</h3>
<p>أفضل مناطق الاستثمار للعائد الإيجاري في 2026:</p>
<ul>
  <li><strong>Dubai Marina</strong> — عائد 6-8%، طلب عالٍ من المغتربين.</li>
  <li><strong>Downtown Dubai</strong> — قرب Burj Khalifa، ROI 5-7%.</li>
  <li><strong>JVC (Jumeirah Village Circle)</strong> — أسعار متوسّطة، ROI 7-9%.</li>
  <li><strong>Business Bay</strong> — منطقة أعمال + سكن، ROI 6-8%.</li>
  <li><strong>MBR City (Meydan)</strong> — مشاريع جديدة، نموّ رأسمالي محتمل عالٍ.</li>
</ul>

<h3>3. اختر خطّة الدفع</h3>
<p>الخطط الشائعة في السوق:</p>
<ul>
  <li><strong>20/80</strong>: دفعة أولى 20% + 80% عند التسليم.</li>
  <li><strong>60/40</strong>: 60% خلال الإنشاء + 40% عند التسليم.</li>
  <li><strong>Post-Handover</strong>: نسبة كبيرة تُدفع بعد التسليم (2-5 سنوات) — أفضل للتدفّق النقدي.</li>
  <li><strong>1% شهريًّا</strong>: خطط بعض المطوّرين لتوزيع الأقساط بشكل مريح.</li>
</ul>

<h3>4. وقّع اتفاقيّة البيع</h3>
<p>الاتفاقيّة تسمّى <em>SPA (Sale &amp; Purchase Agreement)</em>. تحتاج جواز سفر ساري + شيك للدفعة الأولى + رسوم DLD (4% من السعر).</p>

<h3>5. سجّل العقد في دائرة الأراضي</h3>
<p>الخطوة الأهم لحماية حقّك: تسجيل العقد في <strong>Dubai Land Department (DLD)</strong>. هذا يُصدر لك <em>Oqood</em> — شهادة تثبت ملكيّتك المؤقّتة حتى الاستلام.</p>

<h2>حقوقك كمشتري في مشروع على الخارطة</h2>
<ol>
  <li>الحساب المرتبط بمشروعك (Escrow Account) محمي قانونيًّا — المطوّر لا يستطيع سحب المال إلا بالتوازي مع تقدّم البناء.</li>
  <li>لديك حقّ فسخ العقد إذا تأخّر التسليم عن الموعد المتّفق عليه (عادةً بعد 12 شهرًا).</li>
  <li>لديك حقّ إعادة البيع (Assignment) قبل التسليم — لكن قد تفرض شروط.</li>
</ol>

<h2>الإقامة الذهبيّة عبر العقار</h2>
<p>عقار بقيمة <strong>2 مليون درهم أو أكثر</strong> يمنحك حقّ التقديم للإقامة الذهبيّة (10 سنوات، قابلة للتجديد). هذا يشمل زوجك وأبناءك.</p>

<h2>الأخطاء الشائعة التي يجب تجنّبها</h2>
<ul>
  <li><strong>عدم التحقّق من ترخيص المطوّر</strong> — تحقّق دائمًا من <em>RERA</em>.</li>
  <li><strong>الإهمال في قراءة SPA</strong> — تفاصيل التسليم والغرامات والمواصفات مهمّة.</li>
  <li><strong>الاعتماد على وسيط غير مرخّص</strong> — اطلب رقم RERA للوسيط قبل التعامل.</li>
  <li><strong>حساب ROI بشكل خاطئ</strong> — تذكّر رسوم الصيانة والخدمات السنويّة.</li>
</ul>

<h2>لماذا نحن في الزعيم العيناوي؟</h2>
<p>نعمل بأسعار المطوّر مباشرةً — <strong>لا نأخذ منك أيّ عمولة</strong>. مستشارونا يتحدّثون العربيّة، الإنجليزيّة، الروسيّة، والصينيّة. ومكتبنا في شارع الشيخ زايد بإطلالة على متحف المستقبل مفتوح لاستقبالك مع فنجان قهوة.</p>
        `,
        faq: [
            {
                q: 'هل يمكن للأجانب شراء عقارات على الخارطة في دبي؟',
                a: 'نعم، الأجانب يمكنهم التملّك الحرّ في المناطق المخصّصة (Freehold Areas) وهي معظم المناطق الاستثماريّة في دبي: Dubai Marina، Downtown، JVC، Business Bay، Palm Jumeirah وأكثر.'
            },
            {
                q: 'كم تكلفة تسجيل العقار في دائرة الأراضي؟',
                a: 'رسوم تسجيل DLD تبلغ 4% من قيمة العقار. في المشاريع على الخارطة يدفعها المشتري عادةً كاملة عند تسجيل الأوقود، وفي إعادة البيع يُتّفق عليها بين الطرفين. التفاصيل في دليل رسوم شراء عقار في دبي.'
            },
            {
                q: 'ما هو الحدّ الأدنى للإقامة الذهبيّة عبر العقار؟',
                a: 'الحدّ الأدنى 2 مليون درهم إماراتي في عقار واحد أو مجموع عقارات. الإقامة صالحة 10 سنوات وقابلة للتجديد.'
            },
            {
                q: 'ماذا يحدث إذا تأخّر التسليم عن الموعد؟',
                a: 'لك الحقّ في فسخ العقد واسترداد مدفوعاتك بعد فترة السماح (عادةً 12 شهرًا من التاريخ المتّفق عليه)، وفق شروط عقد SPA.'
            },
            {
                q: 'هل يمكن بيع العقار قبل التسليم؟',
                a: 'نعم، عبر عمليّة تُسمّى Assignment. عادةً بعد دفع 30-40% من قيمة العقار، ووفق موافقة المطوّر ورسوم إعادة تسجيل.'
            }
        ]
    },

    {
        slug: 'dubai-marina-investment-guide-ar',
        translationKey: 'dubai-marina-2026',
        lang: 'ar',
        title: 'الاستثمار في دبي مارينا 2026 — الأفضل للعائد الإيجاري؟',
        description: 'دليل الاستثمار في Dubai Marina: العائد الإيجاري، أفضل المشاريع، الأسعار، والمقارنة مع Downtown و JVC.',
        keywords: 'دبي مارينا, استثمار عقاري, عائد إيجاري, شقق للبيع دبي مارينا, ROI Dubai Marina',
        image: '/logo.png',
        publishedAt: '2026-07-10T10:00:00Z',
        updatedAt: '2026-07-15T10:00:00Z',
        readingTime: 6,
        excerpt: 'دبي مارينا لا تزال في المرتبة الأولى للعائد الإيجاري رغم تنافس المناطق الجديدة. نستعرض السبب: الطلب، الأسعار، والمشاريع القادمة.',
        content: `
<p class="lead">إذا كان هدفك عائد إيجاري ثابت وقابل للتنبؤ، فـ Dubai Marina لا يزال في القمّة. رغم صعود مناطق جديدة (MBR City، Dubai Hills، JVC)، لا يزال المستأجرون يفضّلون المارينا لثلاثة أسباب: <strong>الموقع، البنية التحتيّة، وسهولة التأجير</strong>.</p>

<h2>لماذا Dubai Marina لا يزال الأول؟</h2>
<h3>1. الموقع</h3>
<p>على شاطئ الخليج، قريب من Palm Jumeirah و JBR و Media City. الوصول لـ Downtown في 15 دقيقة، ومطار دبي في 25 دقيقة.</p>
<h3>2. المستأجرون</h3>
<p>مغتربون شباب من قطاعات الإعلام والتقنية والماليّة — دخلهم مرتفع ومستقرّ. متوسّط عقد الإيجار 1-2 سنة، ومعدّل الشغور أقلّ من 5%.</p>
<h3>3. المرافق</h3>
<p>Dubai Marina Mall، Marina Walk، شاطئ JBR، مترو + ترام + قوارب مائيّة.</p>

<h2>أرقام الاستثمار الحاليّة (2026)</h2>
<table>
<thead><tr><th>نوع الشقّة</th><th>سعر الشراء</th><th>إيجار سنوي</th><th>عائد ROI</th></tr></thead>
<tbody>
<tr><td>Studio</td><td>800K-1.2M</td><td>60K-80K</td><td>6-7%</td></tr>
<tr><td>1 BR</td><td>1.3M-2M</td><td>90K-130K</td><td>6-7%</td></tr>
<tr><td>2 BR</td><td>2M-3.5M</td><td>140K-200K</td><td>6-7%</td></tr>
<tr><td>3 BR</td><td>3.5M-6M</td><td>220K-350K</td><td>5-6%</td></tr>
</tbody>
</table>

<h2>مشاريع على الخارطة في المارينا 2026</h2>
<p>عدّة مطوّرين يطلقون مشاريع جديدة في المارينا وحولها هذا العام. تواصل معنا للحصول على أحدث القائمة مع خطط الدفع.</p>

<h2>مقارنة سريعة: Marina vs Downtown vs JVC</h2>
<table>
<thead><tr><th>الميزة</th><th>Dubai Marina</th><th>Downtown</th><th>JVC</th></tr></thead>
<tbody>
<tr><td>ROI متوسّط</td><td>6.5%</td><td>5.5%</td><td>7.5%</td></tr>
<tr><td>سعر Studio</td><td>1M</td><td>1.5M</td><td>600K</td></tr>
<tr><td>معدّل الشغور</td><td>&lt; 5%</td><td>&lt; 5%</td><td>7-10%</td></tr>
<tr><td>نموّ رأس المال</td><td>معتدل</td><td>معتدل</td><td>عالٍ</td></tr>
</tbody>
</table>

<h2>الخلاصة</h2>
<p>Dubai Marina خيار "آمن" — عائد ثابت وطلب مستقرّ. للنموّ الرأسمالي الأعلى انظر إلى JVC و MBR City، لكن الشغور أعلى قليلًا. اختر حسب أهدافك: <strong>عائد ثابت (Marina)</strong> أو <strong>نموّ عالٍ (JVC/MBR City)</strong>.</p>
        `,
        faq: [
            { q: 'ما متوسّط سعر الاستوديو في Dubai Marina؟', a: 'يتراوح بين 800,000 و 1.2 مليون درهم للاستوديو الجاهز، أقلّ للمشاريع على الخارطة.' },
            { q: 'كم يستغرق تأجير شقّة في Marina؟', a: 'عادةً أقلّ من 30 يومًا نظرًا للطلب المرتفع من المغتربين.' },
            { q: 'هل Dubai Marina مناسب للسكن العائلي؟', a: 'نعم، خصوصًا شقق 2 BR و 3 BR القريبة من المدارس في Al Sufouh و Media City.' }
        ]
    },

    // ============================================================
    // ENGLISH
    // ============================================================
    {
        slug: 'complete-guide-off-plan-dubai-2026-en',
        translationKey: 'off-plan-guide-2026',
        lang: 'en',
        title: 'Complete Guide to Buying Off-Plan Property in Dubai 2026',
        description: 'Step-by-step guide to buying off-plan property in Dubai 2026: developers, payment plans, Golden Visa, and buyer rights explained.',
        keywords: 'off plan dubai, buy property dubai, dubai real estate 2026, off plan payment plan, golden visa property, dubai property investment',
        image: '/logo.png',
        publishedAt: '2026-07-15T10:00:00Z',
        updatedAt: '2026-07-15T10:00:00Z',
        readingTime: 8,
        excerpt: 'Dubai remains the #1 real estate destination in the Middle East in 2026. Whether you want a home, rental yield, or capital growth, off-plan property delivers the best entry prices. Here is how to buy safely.',
        content: `
<p class="lead">Dubai remains the #1 real estate destination in the Middle East in 2026. Whether your goal is a family home, rental yield, or capital growth, off-plan property gives you the best entry prices and the widest choice. But it requires a clear playbook.</p>

<h2>What is Off-Plan Property?</h2>
<p>Off-plan means you buy directly from the developer while the project is still under construction. You pay in installments spread across the build period (2-4 years) and take possession on completion.</p>

<h3>Why investors prefer it</h3>
<ul>
  <li><strong>20-35% below ready-market prices</strong>.</li>
  <li><strong>Flexible payment plans</strong> — 20/80, 60/40, Post-Handover.</li>
  <li><strong>Capital appreciation</strong> during construction.</li>
  <li><strong>Widest inventory</strong> — pick your unit, floor, and view.</li>
</ul>

<h2>Step-by-step buying process</h2>

<h3>1. Choose the developer</h3>
<p>Dubai's tier-1 developers: <strong>Emaar, DAMAC, Sobha, Nakheel, Meraas, Dubai Properties, Select Group, Azizi</strong>. Each has a distinct style — Emaar dominates Downtown and Marina icons, DAMAC leans luxury and branded residences, Sobha is known for build quality.</p>

<h3>2. Choose the area</h3>
<p>Top rental-yield areas in 2026:</p>
<ul>
  <li><strong>Dubai Marina</strong> — 6-8% ROI, expat demand.</li>
  <li><strong>Downtown Dubai</strong> — 5-7% ROI, icon proximity.</li>
  <li><strong>JVC</strong> — 7-9% ROI, mid-price entry.</li>
  <li><strong>Business Bay</strong> — 6-8% ROI, mixed use.</li>
  <li><strong>MBR City / Meydan</strong> — high capital growth potential.</li>
</ul>

<h3>3. Choose the payment plan</h3>
<ul>
  <li><strong>20/80</strong>: 20% down + 80% on handover.</li>
  <li><strong>60/40</strong>: 60% during construction + 40% on handover.</li>
  <li><strong>Post-Handover</strong>: significant portion paid after handover (2-5 years) — best for cash flow.</li>
  <li><strong>1% monthly plans</strong>: some developers offer very flexible installments.</li>
</ul>

<h3>4. Sign the SPA</h3>
<p>The <em>Sale &amp; Purchase Agreement</em> requires a valid passport, first-installment cheque, and DLD fees (4% of purchase price).</p>

<h3>5. Register with Dubai Land Department</h3>
<p>The critical step: register the contract at <strong>DLD</strong>. You'll receive an <em>Oqood</em> — proof of interim ownership until handover.</p>

<h2>Buyer rights on off-plan</h2>
<ol>
  <li>Your money sits in a legally protected <strong>Escrow Account</strong>. The developer can only draw funds in step with construction progress.</li>
  <li>You have the right to cancel if handover is delayed beyond the SPA grace period (usually 12 months).</li>
  <li>You can resell before handover (Assignment) — usually after paying 30-40%.</li>
</ol>

<h2>Golden Visa through property</h2>
<p>A property investment of <strong>AED 2 million or more</strong> qualifies you for the Golden Visa (10 years, renewable). Includes spouse and children.</p>

<h2>Common mistakes to avoid</h2>
<ul>
  <li>Not verifying the developer with <strong>RERA</strong>.</li>
  <li>Skipping the SPA fine print — handover date, penalties, specs.</li>
  <li>Working with an unlicensed broker — always ask for a RERA number.</li>
  <li>Miscalculating ROI — remember annual service charges (usually 15-25 AED/sqft).</li>
</ul>

<h2>Why work with Al Zaeem Al Ainawi Real Estate?</h2>
<p>We work at direct developer prices — <strong>zero commission from you</strong>. Our advisors speak Arabic, English, Russian, and Chinese. Our Sheikh Zayed Road office overlooks the Museum of the Future — visit us for a coffee and a clear conversation.</p>
        `,
        faq: [
            { q: 'Can foreigners buy off-plan property in Dubai?', a: 'Yes. Freehold ownership is open to all nationalities in designated freehold areas, which cover most investment districts in Dubai.' },
            { q: 'What are DLD registration fees?', a: '4% of the property value, typically split 2/2 between buyer and seller, though usually paid entirely by the buyer.' },
            { q: 'What is the Golden Visa threshold via property?', a: 'AED 2 million minimum in a single property or combined properties. The visa is valid for 10 years and renewable.' },
            { q: 'What happens if handover is delayed?', a: 'You have the right to cancel and reclaim your payments after the SPA grace period, typically 12 months from the agreed handover date.' },
            { q: 'Can I sell before handover?', a: 'Yes, via Assignment. Typically permitted after paying 30-40% of the price, subject to developer approval and re-registration fees.' }
        ]
    },

    {
        slug: 'dubai-marina-investment-guide-en',
        translationKey: 'dubai-marina-2026',
        lang: 'en',
        title: 'Dubai Marina Investment Guide 2026: Best Rental Yield?',
        description: 'Complete Dubai Marina investment analysis: rental yields, top projects, prices, and comparison vs Downtown and JVC.',
        keywords: 'dubai marina investment, dubai marina rental yield, dubai marina apartments, dubai marina ROI, best area invest dubai',
        image: '/logo.png',
        publishedAt: '2026-07-10T10:00:00Z',
        updatedAt: '2026-07-15T10:00:00Z',
        readingTime: 6,
        excerpt: 'Dubai Marina keeps its #1 spot for rental yield even as new districts emerge. Here is why — demand, prices, and upcoming off-plan launches.',
        content: `
<p class="lead">If your goal is a stable, predictable rental yield, Dubai Marina remains at the top. Even with the rise of new areas (MBR City, Dubai Hills, JVC), tenants still prefer the Marina for three reasons: <strong>location, infrastructure, and ease of leasing</strong>.</p>

<h2>Why Dubai Marina still wins</h2>
<h3>1. Location</h3>
<p>Waterfront, next to Palm Jumeirah, JBR, and Media City. 15 minutes to Downtown, 25 minutes to Dubai International Airport.</p>
<h3>2. Tenant profile</h3>
<p>Young expats from media, tech, and finance — high, stable incomes. Average lease is 1-2 years, vacancy under 5%.</p>
<h3>3. Amenities</h3>
<p>Dubai Marina Mall, Marina Walk, JBR Beach, metro + tram + water taxis.</p>

<h2>Current investment numbers (2026)</h2>
<table>
<thead><tr><th>Unit type</th><th>Purchase price</th><th>Annual rent</th><th>ROI</th></tr></thead>
<tbody>
<tr><td>Studio</td><td>AED 800K-1.2M</td><td>60K-80K</td><td>6-7%</td></tr>
<tr><td>1 BR</td><td>AED 1.3M-2M</td><td>90K-130K</td><td>6-7%</td></tr>
<tr><td>2 BR</td><td>AED 2M-3.5M</td><td>140K-200K</td><td>6-7%</td></tr>
<tr><td>3 BR</td><td>AED 3.5M-6M</td><td>220K-350K</td><td>5-6%</td></tr>
</tbody>
</table>

<h2>Off-plan projects in Marina 2026</h2>
<p>Several developers are launching new towers in Marina and immediate surroundings this year. Contact us for the latest inventory with payment plans.</p>

<h2>Quick comparison: Marina vs Downtown vs JVC</h2>
<table>
<thead><tr><th>Metric</th><th>Marina</th><th>Downtown</th><th>JVC</th></tr></thead>
<tbody>
<tr><td>Average ROI</td><td>6.5%</td><td>5.5%</td><td>7.5%</td></tr>
<tr><td>Studio entry</td><td>AED 1M</td><td>AED 1.5M</td><td>AED 600K</td></tr>
<tr><td>Vacancy rate</td><td>&lt; 5%</td><td>&lt; 5%</td><td>7-10%</td></tr>
<tr><td>Capital growth</td><td>Moderate</td><td>Moderate</td><td>High</td></tr>
</tbody>
</table>

<h2>Bottom line</h2>
<p>Dubai Marina is the "safe" choice — steady yield, stable demand. For higher capital growth, look at JVC and MBR City, but expect slightly higher vacancy. Pick based on your goal: <strong>stable yield (Marina)</strong> or <strong>growth (JVC / MBR City)</strong>.</p>
        `,
        faq: [
            { q: 'What is the average studio price in Dubai Marina?', a: 'AED 800,000 to 1.2 million for ready units; off-plan launches start lower.' },
            { q: 'How long does it take to rent an apartment in the Marina?', a: 'Typically under 30 days due to strong expat demand.' },
            { q: 'Is Dubai Marina family-friendly?', a: 'Yes, especially 2 BR and 3 BR units near schools in Al Sufouh and Media City.' }
        ]
    },

    // ============================================================
    // RUSSIAN
    // ============================================================
    {
        slug: 'how-to-buy-property-dubai-russian-2026-ru',
        translationKey: 'off-plan-guide-2026',
        lang: 'ru',
        title: 'Как купить квартиру в Дубае россиянину: гид 2026',
        description: 'Подробное руководство для россиян: покупка недвижимости в Дубае в 2026 году. Застройщики, планы оплаты, Золотая виза, права покупателя.',
        keywords: 'купить квартиру в дубае, недвижимость дубай для россиян, дубай инвестиции, золотая виза оаэ, эмаар квартиры',
        image: '/logo.png',
        publishedAt: '2026-07-15T10:00:00Z',
        updatedAt: '2026-07-15T10:00:00Z',
        readingTime: 9,
        excerpt: 'Дубай остаётся одним из самых востребованных направлений для российских инвесторов в 2026 году. Разбираем, как купить безопасно и с максимальной доходностью.',
        content: `
<p class="lead">Дубай остаётся одним из самых востребованных направлений для российских инвесторов в 2026 году. Стабильная валюта, отсутствие налога на доход от аренды, Золотая виза за покупку от 2 млн AED — веские причины. Ниже — понятное руководство от идеи до подписания SPA.</p>

<h2>Что такое off-plan?</h2>
<p>Off-plan — это покупка квартиры напрямую у застройщика на этапе строительства. Вы платите поэтапно в течение 2-4 лет и получаете ключи по окончании стройки. Цены на 20-35% ниже готового рынка.</p>

<h3>Преимущества для российских инвесторов</h3>
<ul>
  <li>Гибкие планы оплаты — 20/80, 60/40, Post-Handover.</li>
  <li>Прирост капитала во время стройки.</li>
  <li>Право продажи до сдачи (Assignment).</li>
  <li>Оплата в AED или USD — минимальный валютный риск для крупных сумм.</li>
</ul>

<h2>Шаги покупки</h2>

<h3>1. Выбор застройщика</h3>
<p>Топ-застройщики Дубая: <strong>Emaar, DAMAC, Sobha, Nakheel, Meraas, Dubai Properties, Select Group, Azizi</strong>. Emaar доминирует в Downtown и Marina, DAMAC — в люкс-сегменте, Sobha известен качеством стройки.</p>

<h3>2. Выбор района</h3>
<p>Лучшие районы для арендной доходности в 2026:</p>
<ul>
  <li><strong>Dubai Marina</strong> — ROI 6-8%, спрос от экспатов.</li>
  <li><strong>Downtown Dubai</strong> — ROI 5-7%, близость к Burj Khalifa.</li>
  <li><strong>JVC</strong> — ROI 7-9%, средний ценовой сегмент.</li>
  <li><strong>Business Bay</strong> — ROI 6-8%, деловой район.</li>
  <li><strong>MBR City / Meydan</strong> — высокий потенциал роста капитала.</li>
</ul>

<h3>3. План оплаты</h3>
<ul>
  <li><strong>20/80</strong>: 20% первый взнос + 80% при сдаче.</li>
  <li><strong>60/40</strong>: 60% во время стройки + 40% при сдаче.</li>
  <li><strong>Post-Handover</strong>: значительная часть после сдачи (2-5 лет).</li>
  <li><strong>Планы 1% в месяц</strong>: доступны у некоторых застройщиков.</li>
</ul>

<h3>4. Подписание SPA</h3>
<p>Sale &amp; Purchase Agreement подписывается на основании паспорта, первого взноса и оплаты сборов DLD (4% от стоимости).</p>

<h3>5. Регистрация в DLD</h3>
<p>Ключевой шаг для защиты прав: регистрация в <strong>Департаменте земельных ресурсов Дубая (DLD)</strong>. Вы получите <em>Oqood</em> — свидетельство временной собственности до сдачи объекта.</p>

<h2>Права российского покупателя</h2>
<ol>
  <li>Деньги находятся на защищённом <strong>эскроу-счёте</strong>. Застройщик получает средства только по мере готовности стройки.</li>
  <li>Вы можете расторгнуть SPA при задержке сдачи (обычно после 12 месяцев).</li>
  <li>Вы можете перепродать квартиру до сдачи (Assignment), обычно после оплаты 30-40%.</li>
</ol>

<h2>Золотая виза через недвижимость</h2>
<p>Инвестиция от <strong>2 миллионов AED</strong> (около 46 миллионов рублей на дату 2026) даёт право на подачу на Золотую визу — 10 лет с правом продления. Включает супругу/супруга и детей.</p>

<h2>Налоги</h2>
<ul>
  <li>Налог на доход от аренды: <strong>0%</strong> в ОАЭ.</li>
  <li>Налог на прирост капитала при продаже: <strong>0%</strong>.</li>
  <li>Ежегодный муниципальный сбор: 5% от арендной ставки (платит арендатор).</li>
  <li>Обслуживание здания (Service Charge): 15-25 AED за кв.фут в год.</li>
</ul>

<h2>Как переводить деньги из России?</h2>
<p>В 2026 году используются: банковские переводы через Emirates NBD, Mashreq, ADCB (открытие счёта требует визы), крипто-конвертация через лицензированные обменники ОАЭ (VARA-регулируемые), и специализированные payment gateway. Мы поможем подобрать оптимальный канал.</p>

<h2>Наиболее частые ошибки</h2>
<ul>
  <li>Не проверка лицензии застройщика в <strong>RERA</strong>.</li>
  <li>Пропуск деталей SPA — сроки сдачи, штрафы, спецификации.</li>
  <li>Работа с нелицензированным брокером — всегда просите номер RERA.</li>
  <li>Неверный расчёт ROI — не забывайте о сборах и налогах.</li>
</ul>

<h2>Почему выбирают нас?</h2>
<p>Мы работаем по прямым ценам застройщика — <strong>без комиссии с вас</strong>. Наши консультанты говорят на арабском, английском, русском и китайском. Наш офис на Sheikh Zayed Road с видом на Museum of the Future ждёт вас на консультацию за чашкой кофе.</p>
        `,
        faq: [
            { q: 'Могут ли россияне покупать недвижимость в Дубае?', a: 'Да. Freehold-владение открыто для всех национальностей в специально обозначенных районах, которые охватывают большинство инвестиционных зон Дубая.' },
            { q: 'Какая минимальная стоимость для Золотой визы?', a: 'От 2 миллионов AED в одной квартире или сумме нескольких объектов. Виза выдаётся на 10 лет с возможностью продления.' },
            { q: 'Есть ли налог на доход от аренды в Дубае?', a: 'Нет. ОАЭ не взимают налог на доход от аренды и прирост капитала для физических лиц.' },
            { q: 'Что происходит, если сдача задерживается?', a: 'После истечения льготного периода SPA (обычно 12 месяцев с оговорённой даты) вы имеете право расторгнуть договор и вернуть деньги.' },
            { q: 'Как перевести деньги из России в Дубай?', a: 'Через банковские переводы после открытия счёта в ОАЭ, через регулируемые VARA обменники или специализированные payment gateway. Мы поможем подобрать канал.' }
        ]
    },

    {
        slug: 'dubai-marina-investment-ru',
        translationKey: 'dubai-marina-2026',
        lang: 'ru',
        title: 'Инвестиции в Дубай Марина 2026 — Актуальная аналитика',
        description: 'Полный анализ инвестиций в Дубай Марина 2026: арендная доходность, лучшие проекты, цены и сравнение с Downtown и JVC.',
        keywords: 'дубай марина инвестиции, дубай марина аренда, дубай марина квартиры, дубай марина ROI',
        image: '/logo.png',
        publishedAt: '2026-07-10T10:00:00Z',
        updatedAt: '2026-07-15T10:00:00Z',
        readingTime: 6,
        excerpt: 'Дубай Марина сохраняет лидерство по арендной доходности даже при появлении новых районов. Разбираем причины и цифры.',
        content: `
<p class="lead">Если ваша цель — стабильная и предсказуемая арендная доходность, Дубай Марина остаётся в топе. Несмотря на рост новых районов (MBR City, Dubai Hills, JVC), арендаторы по-прежнему выбирают Марину по трём причинам: <strong>расположение, инфраструктура и лёгкость сдачи</strong>.</p>

<h2>Почему Дубай Марина по-прежнему №1</h2>
<h3>1. Расположение</h3>
<p>На побережье, рядом с Palm Jumeirah, JBR и Media City. 15 минут до Downtown, 25 минут до аэропорта.</p>
<h3>2. Профиль арендатора</h3>
<p>Молодые экспаты из медиа, IT и финансов — высокий стабильный доход. Средний срок аренды 1-2 года, вакантность менее 5%.</p>
<h3>3. Инфраструктура</h3>
<p>Dubai Marina Mall, Marina Walk, пляж JBR, метро + трамвай + водные такси.</p>

<h2>Актуальные цифры (2026)</h2>
<table>
<thead><tr><th>Тип квартиры</th><th>Цена покупки</th><th>Годовая аренда</th><th>ROI</th></tr></thead>
<tbody>
<tr><td>Studio</td><td>AED 800K-1.2M</td><td>60K-80K</td><td>6-7%</td></tr>
<tr><td>1 BR</td><td>AED 1.3M-2M</td><td>90K-130K</td><td>6-7%</td></tr>
<tr><td>2 BR</td><td>AED 2M-3.5M</td><td>140K-200K</td><td>6-7%</td></tr>
<tr><td>3 BR</td><td>AED 3.5M-6M</td><td>220K-350K</td><td>5-6%</td></tr>
</tbody>
</table>

<h2>Проекты off-plan в Марине 2026</h2>
<p>В этом году несколько застройщиков запускают новые башни в Марине и вокруг. Свяжитесь с нами для получения актуальной информации с планами оплаты.</p>

<h2>Сравнение: Marina vs Downtown vs JVC</h2>
<table>
<thead><tr><th>Метрика</th><th>Marina</th><th>Downtown</th><th>JVC</th></tr></thead>
<tbody>
<tr><td>Средний ROI</td><td>6.5%</td><td>5.5%</td><td>7.5%</td></tr>
<tr><td>Studio от</td><td>AED 1M</td><td>AED 1.5M</td><td>AED 600K</td></tr>
<tr><td>Вакантность</td><td>&lt; 5%</td><td>&lt; 5%</td><td>7-10%</td></tr>
<tr><td>Рост капитала</td><td>Умеренный</td><td>Умеренный</td><td>Высокий</td></tr>
</tbody>
</table>

<h2>Итог</h2>
<p>Дубай Марина — "безопасный" выбор: стабильный доход, устойчивый спрос. Для более высокого роста капитала смотрите на JVC и MBR City, но с чуть более высокой вакантностью. Выбирайте по цели: <strong>стабильный доход (Marina)</strong> или <strong>рост (JVC / MBR City)</strong>.</p>
        `,
        faq: [
            { q: 'Какая средняя цена студии в Дубай Марина?', a: 'AED 800,000 - 1.2 миллиона для готовых квартир, off-plan начинается ниже.' },
            { q: 'Как быстро можно сдать квартиру в Марине?', a: 'Обычно менее 30 дней благодаря высокому спросу от экспатов.' },
            { q: 'Подходит ли Дубай Марина для семейного проживания?', a: 'Да, особенно 2 BR и 3 BR рядом со школами в Al Sufouh и Media City.' }
        ]
    }
];

export function getPostsByLang(lang) {
    return posts.filter(p => p.lang === lang);
}

export function getPostBySlug(slug) {
    return posts.find(p => p.slug === slug);
}

/** Languages that have at least one post — the blog index exists in these. */
export const BLOG_LANGS = [...new Set(posts.map((p) => p.lang))];

/**
 * { lang: '/blog/<slug>' } for every language version of a post, itself
 * included. Paths are unprefixed; the language decides the prefix.
 */
export function postAlternates(post) {
    if (!post) return {};
    const versions = post.translationKey
        ? posts.filter((p) => p.translationKey === post.translationKey)
        : [post];
    return Object.fromEntries(versions.map((p) => [p.lang, `/blog/${p.slug}`]));
}

export function getRelatedPosts(currentSlug, lang, limit = 3) {
    return posts
        .filter(p => p.slug !== currentSlug && p.lang === lang)
        .slice(0, limit);
}

export default posts;
